import {
  type MaybeRefOrGetter,
  onBeforeUnmount,
  onMounted,
  toValue,
} from 'vue';
import { sentryService } from '@/services/telemetry/sentry';
import { track, warnTelemetryFailure } from '@/shared/utils/track-telemetry';
import type {
  LegacyGalleryItem,
  LegacyGallerySelectionMode,
} from './legacyGalleryTypes';

const CLICK_DEBOUNCE_MS = 300;
const SLIDE_TELEMETRY_THROTTLE_MS = 500;

const SENSITIVE_URL_PARAMS = new Set([
  'token',
  'access_token',
  'refresh_token',
  'id_token',
  'client_secret',
  'password',
  'signature',
  'x-amz-signature',
  'x-amz-credential',
  'x-amz-security-token',
]);

export interface UseLegacyGalleryTelemetryOptions<T extends LegacyGalleryItem> {
  actualItemsPerSlide: MaybeRefOrGetter<number>;
  isGridMode: MaybeRefOrGetter<boolean>;
  items: MaybeRefOrGetter<readonly T[]>;
  onImageError?: (item: T, index: number, event: Event) => void;
  selectedItemUuids: MaybeRefOrGetter<readonly (string | undefined)[]>;
  selectionMode: MaybeRefOrGetter<LegacyGallerySelectionMode>;
}

export type LegacyGallerySelectionChange =
  | { isSelected: boolean; mode: 'single' }
  | { isSelected: boolean; mode: 'multiple'; totalSelected: number };

export interface LegacyGallerySlideChange<T extends LegacyGalleryItem> {
  delta: number;
  firstVisibleItemIndex: number;
  visibleItems: Array<T | undefined>;
}

export interface LegacyGalleryTelemetry<T extends LegacyGalleryItem> {
  flushGalleryMetrics: () => Promise<void>;
  leave: () => void;
  recordImageLoadError: (item: T, index: number, event: Event) => void;
  recordItemClick: (event: Event, item: T) => boolean;
  recordSelection: (change: LegacyGallerySelectionChange) => void;
  recordSlideSwitch: (change: LegacyGallerySlideChange<T>) => void;
  sanitizeUrl: (url: string) => string;
}

interface GalleryMetrics {
  failedItemUuids: Set<string>;
  imageLoadErrors: number;
  itemsClicked: number;
  itemsSelected: number;
  slidesSwitched: number;
}

export function sanitizeLegacyGalleryUrl(url: string): string {
  if (!url) {
    return '';
  }
  try {
    const parsed = new URL(url, window.location.origin);
    for (const key of [...parsed.searchParams.keys()]) {
      if (SENSITIVE_URL_PARAMS.has(key.toLowerCase())) {
        parsed.searchParams.set(key, '[REDACTED]');
      }
    }
    return url.startsWith('/')
      ? `${parsed.pathname}${parsed.search}${parsed.hash}`
      : parsed.toString();
  } catch {
    return url.replace(
      /([?&](?:token|access_token|refresh_token|id_token|client_secret|password|signature|x-amz-signature|x-amz-credential|x-amz-security-token)=)[^&#\s]*/gi,
      '$1[REDACTED]'
    );
  }
}

export function useLegacyGalleryTelemetry<T extends LegacyGalleryItem>(
  options: UseLegacyGalleryTelemetryOptions<T>
): LegacyGalleryTelemetry<T> {
  const metrics: GalleryMetrics = {
    failedItemUuids: new Set(),
    imageLoadErrors: 0,
    itemsClicked: 0,
    itemsSelected: 0,
    slidesSwitched: 0,
  };
  let lastClickedUuid: string | null | undefined = null;
  let lastClickTime = 0;
  let lastSentryScrollTime = 0;

  function readItems(): readonly T[] {
    return toValue(options.items);
  }

  function readSelectionMode(): LegacyGallerySelectionMode {
    return toValue(options.selectionMode);
  }

  function recordMounted(): void {
    track('[ImageGallery] Sentry breadcrumb failed:', () => {
      sentryService.addBreadcrumb('ui.gallery', 'ImageGallery mounted', {
        itemCount: readItems().length,
        itemsPerSlide: toValue(options.actualItemsPerSlide),
        selectionMode: readSelectionMode() || 'none',
        isGridMode: toValue(options.isGridMode),
      });
    });
  }

  function recordItemClick(event: Event, item: T): boolean {
    const now = Date.now();
    if (
      item.uuid === lastClickedUuid &&
      now - lastClickTime < CLICK_DEBOUNCE_MS
    ) {
      event.preventDefault();
      return false;
    }
    lastClickedUuid = item.uuid;
    lastClickTime = now;

    const itemIndex = readItems().findIndex(
      (candidate) => candidate.uuid === item.uuid
    );
    const selectionMode = readSelectionMode() || 'none';
    metrics.itemsClicked++;
    track('[ImageGallery] Sentry tracking failed:', () => {
      sentryService.addBreadcrumb('ui.gallery', 'Gallery item clicked', {
        itemUuid: item.uuid,
        itemIndex,
        selectionMode,
        wasSelected: toValue(options.selectedItemUuids).includes(item.uuid),
      });
      sentryService.incrementCounter('gallery.item_clicked', 1, {
        selection_mode: selectionMode,
      });
    });
    return true;
  }

  function recordSelection(change: LegacyGallerySelectionChange): void {
    if (!change.isSelected) {
      return;
    }
    metrics.itemsSelected++;
    track('[ImageGallery] Sentry counter failed:', () => {
      sentryService.incrementCounter(
        'gallery.item_selected',
        1,
        change.mode === 'single'
          ? { mode: 'single' }
          : { mode: 'multiple', total_selected: change.totalSelected }
      );
    });
  }

  function recordSlideSwitch(change: LegacyGallerySlideChange<T>): void {
    metrics.slidesSwitched++;
    const now = Date.now();
    if (now - lastSentryScrollTime < SLIDE_TELEMETRY_THROTTLE_MS) {
      return;
    }
    lastSentryScrollTime = now;
    const direction = change.delta > 0 ? 'next' : 'prev';
    track('[ImageGallery] Sentry scroll tracking failed:', () => {
      sentryService.addBreadcrumb('ui.gallery', 'Gallery slide switched', {
        direction,
        firstVisibleIndex: change.firstVisibleItemIndex,
        visibleCount: change.visibleItems.length,
        totalItems: readItems().length,
      });
      sentryService.incrementCounter('gallery.slide_switched', 1, {
        direction,
      });
    });
  }

  function recordImageLoadError(item: T, index: number, event: Event): void {
    metrics.imageLoadErrors++;
    if (item.uuid) {
      metrics.failedItemUuids.add(item.uuid);
    }

    const totalItems = readItems().length;
    const uniqueFailedItems = metrics.failedItemUuids.size;
    const errorContext = {
      itemUuid: item.uuid,
      itemIndex: index,
      imageUrl: item.url ? sanitizeLegacyGalleryUrl(item.url) : 'unknown',
      totalItems,
      uniqueFailedItems,
      isGridMode: toValue(options.isGridMode),
    };

    track('[ImageGallery] Sentry error tracking failed:', () => {
      sentryService.addBreadcrumb(
        'ui.gallery',
        'Image load failed',
        errorContext,
        'error'
      );
      sentryService.incrementCounter('gallery.image_load_error', 1, {
        has_url: !!item.url,
      });

      const majorityFailed = uniqueFailedItems / Math.max(totalItems, 1) >= 0.5;
      if (uniqueFailedItems >= 3 && majorityFailed) {
        const error = new Error(
          `Multiple image load failures in gallery (${uniqueFailedItems}/${totalItems} items)`
        );
        error.name = 'GalleryImageLoadError';
        sentryService.captureException(error, {
          tags: {
            component: 'ImageGallery',
            error_type: 'image_load',
          },
          extra: {
            ...errorContext,
            totalErrorEvents: metrics.imageLoadErrors,
          },
          level: 'warning',
        });
      }
    });

    options.onImageError?.(item, index, event);
  }

  async function flushGalleryMetrics(): Promise<void> {
    if (
      metrics.slidesSwitched > 0 ||
      metrics.itemsClicked > 0 ||
      metrics.itemsSelected > 0 ||
      metrics.imageLoadErrors > 0
    ) {
      track('[ImageGallery] Sentry metric tracking failed:', () => {
        sentryService.trackMetric(
          'gallery.session_slides_switched',
          metrics.slidesSwitched,
          'none',
          { component: 'ImageGallery' }
        );
        sentryService.trackMetric(
          'gallery.session_items_clicked',
          metrics.itemsClicked,
          'none',
          { component: 'ImageGallery' }
        );
        sentryService.trackMetric(
          'gallery.session_items_selected',
          metrics.itemsSelected,
          'none',
          { component: 'ImageGallery' }
        );
        if (metrics.imageLoadErrors > 0) {
          sentryService.trackMetric(
            'gallery.session_image_errors',
            metrics.imageLoadErrors,
            'none',
            { component: 'ImageGallery' }
          );
        }
      });
    }

    try {
      await sentryService.flush(500);
    } catch (error) {
      warnTelemetryFailure('[ImageGallery] Sentry flush failed:', error);
    }
  }

  function leave(): void {
    void flushGalleryMetrics();
    track('[ImageGallery] Sentry breadcrumb failed:', () => {
      sentryService.addBreadcrumb('ui.gallery', 'ImageGallery unmounted', {
        totalSlidesSwitched: metrics.slidesSwitched,
        totalItemsClicked: metrics.itemsClicked,
        totalItemsSelected: metrics.itemsSelected,
        totalImageErrors: metrics.imageLoadErrors,
      });
    });
  }

  onMounted(recordMounted);
  onBeforeUnmount(leave);

  return {
    flushGalleryMetrics,
    leave,
    recordImageLoadError,
    recordItemClick,
    recordSelection,
    recordSlideSwitch,
    sanitizeUrl: sanitizeLegacyGalleryUrl,
  };
}
